import { useFeatureFlagPayload } from '@/app-shell/flags/posthog-compat';

/**
 * Copy experiment on the Heidi II welcome step's primary CTA ("See how it
 * works"). PostHog-resolved, so the getter stays app-side — `@heidi/agents/flags`
 * is an OpenFeature door and cannot read PostHog.
 *
 * The arm's copy is the flag PAYLOAD (`{ "cta": "…" }`) rather than the variant
 * key, so a new wording is a PostHog edit and not a deploy. PostHog hands the
 * payload back as a JSON STRING on the flag-details path and as a parsed object
 * on the legacy one, so both are accepted — a string-only check silently served
 * control to everyone.
 *
 * The English gate lives HERE rather than at each of the two hosts, but the
 * locale is PASSED IN rather than read off `useIntl`: a flag getter that needs
 * intl context in its ancestry is a hidden coupling for every caller, and the
 * dev-tools preview host has none.
 *
 * NESTED under `release-heidi-2-onboarding`: the welcome step only renders
 * from the first-run `Heidi2OnboardingHost`, which `CloudHeidi2AwarenessBanner`
 * mounts only while `useIsHeidi2OnboardingEnabled()` is on (never on-prem or on
 * Apple-compliant builds). Relaunches open at `handoff` and never show the CTA.
 *
 * | release-heidi-2-onboarding | this flag   | welcome CTA                       |
 * | -------------------------- | ----------- | --------------------------------- |
 * | OFF                        | any         | not shown (experiment inert)      |
 * | ON                         | control/off | "See how it works" (authored msg) |
 * | ON                         | test arm    | payload `cta` (English only)      |
 *
 * Enrollment is therefore scoped to the release cohort, not a random sample of
 * all users: hold the release rollout steady while the experiment runs, or the
 * population shifts under it.
 */
export const HEIDI_2_WELCOME_CTA_FLAG_KEY = 'exp-heidi-2-welcome-cta-copy';

const readCta = (payload: unknown): string | undefined => {
  const parsed = typeof payload === 'string' ? safeParse(payload) : payload;
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return undefined;
  const cta = (parsed as { cta?: unknown }).cta;
  return typeof cta === 'string' && cta.trim() ? cta : undefined;
};

const safeParse = (raw: string): unknown => {
  try {
    return JSON.parse(raw);
  } catch {
    return undefined;
  }
};

export const useHeidi2WelcomeCtaCopy = (locale: string): string | undefined => {
  const payload = useFeatureFlagPayload(HEIDI_2_WELCOME_CTA_FLAG_KEY);
  return locale.startsWith('en') ? readCta(payload) : undefined;
};
